import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import {
  FormControl,
  FormControlStatus,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { CtDocument } from '@ps-gui/claims-terminal-shared';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DevToolsCardService,
  DropdownComponent,
  DropdownOption,
  DynamicFormComponent,
  DynamicFormService,
  errorText,
  SimpleFormModel,
} from '@ps-gui/ui';
import { KeyValue, NgClass } from '@angular/common';
import { FocusDirective, Keys } from '@ps-gui/shared-core';
import { mapToFormModel } from './form-model.mapper';
import { FieldGroupModel, FieldModel } from '../../../models/extraction-models/extraction-models';
import { ExtractionField } from '../../../pages/extraction-page/extraction-page.component';
import { FieldFormControl } from '../../../models/extraction-form-models';
import { ExtractedValuesByKey } from '../../../models/extracted-values';
import { OcrHighlightService } from '../../../service/overlay/ocr-highlight.service';
import { INPUT_STYLING_SERVICE } from '../../../service/input-styling.service';
import { isConsolidationView } from '../../../models/app-view';
import { WorkflowService } from '../../../service/app-state/workflow.service';
import { DatepickerComponent, DpISelectionEvent } from '@vydaps/ps-date-picker';
import { ManualInputChangeService } from '../../../service/manual-input-change.service';
import { RemoveWrappingCommaValueAccessor } from '../../ui/remove-wrapping-comma-value-accessor.directive';
import { CtBatchStoreService } from '../../../service/app-state/ct-batch-store.service';
import { FieldType } from '@ps-gui/shared-utils';
import { Tooltip } from 'primeng/tooltip';
import { FieldGroupPanelComponent } from '../../../dev-tools/panels/field-group-panel.component';
import { FillMode, generateValues } from '../../../dev-tools/generators/field-value.generator';
import { PrefillResults, PrefillSource } from '../../../service/prefill.service';

@Component({
  selector: 'ct-extraction-field-group-extraction-view',
  templateUrl: './field-group-extraction-view.component.html',
  styleUrl: './field-group-extraction-view.component.scss',
  imports: [
    ReactiveFormsModule,
    DynamicFormComponent,
    NgClass,
    FocusDirective,
    DatepickerComponent,
    RemoveWrappingCommaValueAccessor,
    DropdownComponent,
    Tooltip,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldGroupExtractionViewComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly devToolsCardService = inject(DevToolsCardService);
  private readonly ctBatchStoreService = inject(CtBatchStoreService);
  private readonly highlightService = inject(OcrHighlightService);
  private readonly dynamicFormService = inject(DynamicFormService);
  private readonly inputStylingService = inject(INPUT_STYLING_SERVICE);
  private readonly workflowService = inject(WorkflowService);
  protected readonly manualInputChangeService = inject(ManualInputChangeService);

  readonly document = input<CtDocument>();
  readonly fieldGroupModel = input.required<FieldGroupModel>();
  readonly isActiveSection = input.required<boolean>();
  readonly isConsolidation = input.required<boolean>();
  readonly focusInputs = input.required<boolean>();
  readonly directValueChangeFields = input<string[][]>([]);

  protected readonly visibleFieldModels = computed(() =>
    this.fieldGroupModel()
      .fieldModels.filter((field) => {
        if (!field.visibleForSource) {
          return true;
        }
        return field.visibleForSource.includes(this.ctBatchStoreService.ctBatch.source);
      })
      .filter((field) => this.isConsolidation() || !field.onlyVisibleInConsolidation),
  );

  protected readonly formModel = computed(() =>
    mapToFormModel({ ...this.fieldGroupModel(), fieldModels: this.visibleFieldModels() }),
  );

  readonly formFieldMapChanged = output<Record<string, ElementRef>>();

  protected readonly dynamicForm = computed(() => {
    const dynamicForm = this.dynamicFormService.buildForm(
      [this.formModel()],
      this.prefillResults().fields,
    );
    const formGroup = dynamicForm.formGroup;

    formGroup.statusChanges
      .pipe(
        startWith(formGroup.status),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((status) => this.statusChange.emit(status));

    formGroup.valueChanges
      .pipe(startWith(formGroup.value), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onFormChanged(formGroup));

    for (const key of this.directValueChangeFields()) {
      formGroup
        .get(key)
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
        .subscribe((value) => this.directValueChanged.emit({ key: key[1], value }));
    }

    return dynamicForm;
  });

  readonly currentFieldModel = input<FieldModel>();
  readonly currentFieldKey = computed(() => this.currentFieldModel()?.key);
  readonly documentId = computed(() => this.document()?.id);

  readonly prefillResults = input.required<PrefillResults>();

  readonly continue = output();
  readonly currentExtractionFieldChange = output<ExtractionField | undefined>();
  readonly statusChange = output<FormControlStatus>();
  readonly valueChange = output<ExtractedValuesByKey>();
  readonly directValueChanged = output<KeyValue<string, string>>();

  readonly showHeader = input<boolean>(true);
  readonly headerIcon = input<string | undefined>();
  readonly inputWidth = input<string>('w-full');

  constructor() {
    effect(() => {
      const documentId = this.documentId();
      const key = this.currentFieldKey();
      if (!(key && documentId)) return;

      this.highlightService.setFieldHighlight(documentId, key);
    });

    effect(() => {
      const currentFieldModel = this.currentFieldModel();
      const dynamicForm = this.dynamicForm();
      if (!currentFieldModel || !dynamicForm) return;

      const model = (dynamicForm?.formModels[0] as SimpleFormModel).fieldModels.find(
        (model) => model.key === currentFieldModel.key,
      );

      if (model) {
        this.currentExtractionFieldChange.emit({
          model: currentFieldModel,
          control: model.control!,
        });
      }
    });

    Keys.Enter.down.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // within the single field extraction all the key down event is handled by the field extraction overlay
      if (this.isActiveSection() && isConsolidationView(this.workflowService.currentView()))
        this.continue.emit();
    });
  }

  // The group model is a required input, so the earliest point to name the provider is here.
  ngOnInit() {
    const id = `field-group-${this.documentId() ?? 'standalone'}-${this.fieldGroupModel().key}`;

    this.devToolsCardService.register({
      id,
      label: this.fieldGroupModel().label,
      panel: FieldGroupPanelComponent,
      apply: (mode: FillMode) => this.applyGeneratedValues(mode),
    });

    this.destroyRef.onDestroy(() => this.devToolsCardService.unregister(id));
  }

  private applyGeneratedValues(mode: FillMode): void {
    const groupControl = this.dynamicForm().formGroup.get(this.fieldGroupModel().key);
    if (!groupControl) return;

    groupControl.patchValue(generateValues(this.visibleFieldModels(), mode));
  }

  private onFormChanged(formGroup: UntypedFormGroup) {
    if (formGroup.status === 'VALID') {
      // TODO: this is weird because the form group looks like this: { groupKey: { ... fields ... }
      //  but we actually dont need this groupKey nesting (because each group is a single
      //  group extraction view right now)...
      type ExtractedFormDataValuesByKey = Record<
        string,
        string | number | null | DropdownOption<string>
      >;
      const formData = Object.values(formGroup.value)[0] as ExtractedFormDataValuesByKey;

      for (const key in formData) {
        const currentValue = formData[key];
        if (this.isDropdownOption(currentValue)) {
          formData[key] = currentValue?.value;
        }
      }

      this.valueChange.emit(formData as ExtractedValuesByKey);
    }
  }

  private isDropdownOption(
    extractedValue: string | number | null | DropdownOption<string>,
  ): extractedValue is DropdownOption<string> {
    return (<DropdownOption<string>>extractedValue)?.value !== undefined;
  }

  onFieldFocused(event: unknown, fieldKey: string, control: FieldFormControl) {
    this.currentExtractionFieldChange.emit({
      model: this.fieldGroupModel().fieldModels.find((field) => field.key === fieldKey)!,
      control,
    });
  }

  /** The marker only holds while the field still carries the value the Altfall put there. */
  protected isUnchangedFromPrefilledSource(fieldKey: string, control: FormControl): boolean {
    const prefilledValue = this.prefillResults().fields[fieldKey];
    const prefilledSource = this.prefillResults().sources[fieldKey];
    return !!prefilledSource && control.value === prefilledValue;
  }

  protected getPrefillSourceTooltip(fieldKey: string): string {
    const prefilledSource = this.prefillResults().sources[fieldKey];

    switch (prefilledSource) {
      case PrefillSource.ALTFALL:
        return 'Aus dem zugeordneten Altfall vorbelegt';
      case PrefillSource.BEAUFTRAGUNG:
        return 'Aus der Beauftragung vorbelegt';
      default:
        return '';
    }
  }

  shouldDisplayError(control: FormControl) {
    return control.invalid && control.touched;
  }

  getInputClasses(control: FormControl, isFocused: boolean, autoGrowHeight = false) {
    const displayError = this.shouldDisplayError(control);
    return this.inputStylingService.getDefaultInputClasses(
      control.value,
      isFocused,
      displayError,
      autoGrowHeight,
    );
  }

  protected readonly errorText = errorText;

  handleDatePickerSelectEvent(fieldModel: FieldModel, event: DpISelectionEvent) {
    // select event gets triggered already on setup, but we don't want this first call to trigger any method
    if (event.granularity === null) return;

    this.manualInputChangeService.onUserInput(this.documentId(), fieldModel);
    this.continue.emit();
  }

  protected readonly FieldType = FieldType;
}
